<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useModal } from 'vue-final-modal';
import BiiDatasetEditorPopup from '~/bi/components/dataset-picker/dataset-picker-upload/bi-dataset-editor-popup.vue';
import { useBIDatasetPicker } from '~/bi/composables/useBIDatasetPicker.js';
import { useBiStore } from '~/bi/store/bi.store';
import HawkDeletePopup from '~/common/components/organisms/hawk-delete-popup.vue';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';

const props = defineProps({
  allSchemas: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['selected']);

const { $t, $services } = useCommonImports();
const bi_store = useBiStore();
const table_columns = [
  {
    accessorKey: 'label',
    header: $t('Dataset name'),
    id: 'label',
  },
  {
    accessorKey: 'columns',
    header: $t('Columns'),
    id: 'columns',
  },
  { accessorKey: 'context_menu', header: '', id: 'context_menu', size: '5', show_on_hover: 'true' },
];

/**
 * Build a hierarchical tree from flat schemas where each schema has a `path` array.
 * For a path like ['a','b','c'] we create nodes a -> b -> c, and attach the actual schema
 * as a child leaf node under the final folder node. This ensures folder nodes (a, b, c)
 * are folders and do not themselves carry schema columns — the schema objects are leaves.
 */

const schemas = computed(() => {
  return props.allSchemas;
});

const { hierarchicalSchemas } = useBIDatasetPicker(schemas);

function getChildren(row) {
  // hawk-table may pass either the raw node or a wrapper; normalize access
  const node = row?.data?.row?.original ? row.data.row.original : row;
  return node?.children && node.children.length ? node.children : null;
}

function getColumnsForRow(row) {
  const original = row?.data?.row?.original;
  const node = original ?? row;
  return node?._schema?.columns || node?.columns || [];
}

/**
 * Only emit `selected` for actual schema rows (leaf nodes that carry the original schema object)
 */
const table_instance = ref(null);

function onRowClicked(row) {
  const original = row?.data?.row?.original;
  const node = original ?? row;
  if (node && node._schema) {
    emit('selected', node._schema);
  }
  else {
    table_instance.value.toggleExpandedRow(row.uid);
  }
}

const search = ref('');

function filterTree(nodes = [], term) {
  const lowered = (term || '').toLowerCase();
  const res = [];
  nodes.forEach((node) => {
    const labelMatch = node?.label?.toLowerCase()?.includes(lowered);
    const children = Array.isArray(node.children) && node.children.length ? filterTree(node.children, term) : [];
    if (labelMatch || children.length) {
      const cloned = { ...node };
      if (children.length)
        cloned.children = children;
      else delete cloned.children;
      res.push(cloned);
    }
  });
  return res;
}

const searched_hierarchical_schema = computed(() => {
  if (search.value) {
    return filterTree(hierarchicalSchemas.value || [], search.value);
  }
  return hierarchicalSchemas.value;
});

function onTableInstance(instance) {
  table_instance.value = instance;
  if (search.value) {
    nextTick(() => {
      table_instance.value?.expandAll();
    });
  }
}

function handleEditCustomTable(table_schema) {
  const { open: openBiDatasetEditor, close: closeBiDatasetEditor } = useModal({
    component: BiiDatasetEditorPopup,
    attrs: {
      tableSchema: table_schema,
      onClose() {
        closeBiDatasetEditor();
      },
      onPublished() {
        closeBiDatasetEditor();
      },
    },
  });
  openBiDatasetEditor();
}

function handleDeleteCustomTable(table_schema) {
  const { open: openDeletePopup, close: closeDeletePopup } = useModal({
    component: HawkDeletePopup,
    attrs: {
      show_toast: true,
      onClose() {
        closeDeletePopup();
      },
      confirm: async () => {
        try {
          await $services.data_warehouse.deleteCustomDataset({
            dataset_id: table_schema.name,
          });
          const table_schema_index = bi_store.all_schemas.findIndex(schema => schema.name === table_schema.name);
          if (table_schema_index !== -1)
            bi_store.all_schemas.splice(table_schema_index, 1);
        }
        catch (error) {
          logger.log('Error', error);
        }
        closeDeletePopup();
      },
    },
  });
  openDeletePopup();
}

watch(search, (val) => {
  if (!table_instance.value)
    return;
  if (val) {
    nextTick(() => table_instance.value?.expandAll());
  }
  else {
    nextTick(() => table_instance.value?.collapseAll());
  }
});
</script>

<template>
  <div class="flex justify-start ml-8 mb-4">
    <hawk-search-input v-model="search" :placeholder="$t('Search')" class="my-[1px]" />
  </div>
  <div class="h-[calc(100vh-180px)] overflow-auto">
    <hawk-table
      :columns="table_columns"
      :data="searched_hierarchical_schema"
      :get-children="getChildren"
      :show_menu_header="false"
      class="w-full min-h-[600px]"
      @row-clicked="onRowClicked"
      @table-instance="onTableInstance"
    >
      <template #columns="row">
        <div class="flex flex-wrap gap-2">
          <hawk-badge-group
            :items="getColumnsForRow(row).map(col => ({ uid: col.label, label: col.label }))"
            color="gray"
            :max_badges_to_display="10"
            class="mr-2"
            @click="onRowClicked(row)"
          />
        </div>
      </template>
      <template #context_menu="{ data: { row: { original } } }">
        <div v-if="!original?.children?.length && original?._schema?.path?.includes('Custom')">
          <div class="flex items-center">
            <HawkButton type="plain" icon @click.stop="handleEditCustomTable(original?._schema)">
              <IconHawkPencilOne class="w-4 h-4 text-gray-900" />
            </HawkButton>
            <HawkButton type="plain" icon @click.stop="handleDeleteCustomTable(original?._schema)">
              <IconHawkTrashThree class="w-4 h-4 text-gray-900" />
            </HawkButton>
          </div>
        </div>
      </template>
    </hawk-table>
  </div>
</template>
